import { func, object } from 'prop-types'
import { useEffect, useState } from 'react'
import { Button, kuiperClassNames, PreviewUploader } from '@opallabs/kuiper'
import { getNestedProperty } from '@opallabs/lambda'
import { AnalyticsLayer } from '@opallabs/quanta'
import { assetProxyPreviewUrl } from '@opallabs/routing'
import { selectorHelpers } from '@opallabs/wormhole'
import Buttons from './Buttons'
import ImageViewer from './ImageViewer'
import PreviewCard from './PreviewCard'

import './styles.scss'

const getImageUrl = (asset) => {
  const previewUrl = getNestedProperty(asset, 'preview_url')
  const fullUrl = getNestedProperty(asset, 'full_url')

  if (previewUrl && previewUrl.indexOf('http') !== -1) {
    return previewUrl
  }

  return fullUrl || null
}

const getAssetReferencesWithCoverImages = (momentAssetReferences) =>
  momentAssetReferences.reduce((acc, currentAssetReference) => {
    if (currentAssetReference.cover_asset_reference) {
      acc.push(currentAssetReference.cover_asset_reference)
    } else {
      acc.push(currentAssetReference)
    }
    return acc
  }, [])

const MomentAssetPicker = ({
  bbUnmountModal = () => {},
  moment,
  onClose = () => {},
  onSaveAsset,
  uploadAsset,
}) => {
  let assetReferences = []

  if (moment && moment.asset_references) {
    assetReferences = getAssetReferencesWithCoverImages(moment.asset_references)
  }

  const getAsset = (assetId) => {
    if (
      moment.primary_asset_reference &&
      moment.primary_asset_reference.id === assetId
    ) {
      return moment.primary_asset_reference
    }

    return assetReferences.find(
      (assetReference) => assetReference.id === assetId
    )
  }

  const getCurrentCoverAsset = () => {
    if (!moment || !moment.primary_asset_reference) return {}

    return (
      moment.primary_asset_reference.cover_asset_reference ||
      moment.primary_asset_reference
    )
  }
  // The "cover asset" is able to be uploaded by a user and saved as the
  // moment's primary_asset_reference, but will NOT be added to the
  // asset_references array. If the primary asset is not a part of
  // asset_references, then the cover image was previously uploaded in this picker.
  const currentCoverAsset = getCurrentCoverAsset(moment)
  const currentCoverIsAssetReference =
    assetReferences &&
    assetReferences.some((ar) => ar.id === currentCoverAsset.id)

  const [selectedAssetId, setSelectedAssetId] = useState(
    currentCoverAsset.id || null
  )
  const [coverAssetRemoved, setCoverAssetRemoved] = useState(
    !currentCoverAsset.id || currentCoverIsAssetReference
  )
  const [saveable, setSaveable] = useState(false)
  const [loading, setLoading] = useState(false)
  const [uploaderSelected, setUploaderSelected] = useState(
    !currentCoverIsAssetReference
  )
  const [userUploadedFile, setUserUploadedFile] = useState(null)

  const selectedAsset = getAsset(selectedAssetId)

  useEffect(() => {
    setSaveable(selectedAssetId !== currentCoverAsset.id)
  }, [currentCoverAsset.id, selectedAssetId, setSaveable])

  const handleImageRemoval = () => {
    if (!coverAssetRemoved) setCoverAssetRemoved(true)

    if (uploaderSelected) {
      setUploaderSelected(false)
      setSelectedAssetId(null)
    }

    if (userUploadedFile) {
      setUserUploadedFile(null)
    }
  }

  const handleImageAddition = (file) => {
    setUserUploadedFile(file)
    setUploaderSelected(true)
    setSelectedAssetId(null)
  }

  const handleUploadImage = (file) => {
    if (file === '') {
      handleImageRemoval()
    } else {
      handleImageAddition(file)
    }
  }

  const onFileUpload = (data) => {
    const assetId = data.data.id
    const assetUrl = data.data.attributes.full_url

    onSaveAsset({
      moment,
      selectedAssetId: assetId,
      selectedAssetUrl: assetUrl,
    })
  }

  const onClickSave = () => {
    setLoading(true)
    setSaveable(false)

    const shouldUploadNewFile = Boolean(userUploadedFile && uploaderSelected)

    if (shouldUploadNewFile) {
      uploadAsset({ file: userUploadedFile, onUploadSuccess: onFileUpload })
    } else if (!selectedAssetId) {
      onSaveAsset({
        moment,
        selectedAssetId: null,
        selectedAssetUrl: null,
      })
    } else {
      onSaveAsset({
        moment,
        selectedAssetId: selectedAsset.id,
        selectedAssetUrl: getImageUrl(selectedAsset),
      })
    }

    bbUnmountModal()
  }

  const makePreviewMoment = () => {
    let uploadPreview

    if (!selectedAssetId && !uploaderSelected) {
      uploadPreview = {}
    } else if (uploaderSelected && userUploadedFile) {
      uploadPreview = {
        url: URL.createObjectURL(userUploadedFile),
        title: userUploadedFile.name,
        alt: userUploadedFile.name || 'User uploaded cover image',
      }
    } else {
      const assetReference = getAsset(selectedAssetId)
      uploadPreview = selectorHelpers.getRelevantImageProperties(
        moment,
        assetReference
      )
    }

    return {
      ...moment,
      uploadPreview,
    }
  }

  const handleAssetSelection = (assetId) => {
    setSelectedAssetId(assetId)
    setUploaderSelected(false)
  }

  // With no selection, saving clears the cover and the moment shows the image
  // from its most recently edited content.
  const handleLatestContentImageSelection = () => {
    setSelectedAssetId(null)
  }

  const handleUploaderSelection = () => {
    setUploaderSelected(true)
    if (!coverAssetRemoved) {
      setSelectedAssetId(currentCoverAsset.id)
    } else {
      setSelectedAssetId(null)
    }
  }

  const previewUploaderSrc = () => {
    if (!coverAssetRemoved) {
      return assetProxyPreviewUrl({ uuid: currentCoverAsset.uuid })
    }

    return userUploadedFile && URL.createObjectURL(userUploadedFile)
  }

  return (
    <AnalyticsLayer name="Moment Asset Picker">
      <div className={kuiperClassNames('moment-asset-picker')}>
        <div className={kuiperClassNames('left-panel')}>
          <h3>Choose moment card image</h3>

          <ImageViewer
            assets={assetReferences}
            className={kuiperClassNames('image-viewer')}
            selectedAssetId={selectedAssetId}
            setSelectedAssetId={handleAssetSelection}
          />

          {selectedAssetId && !uploaderSelected && (
            <Button
              label="Use latest content image"
              onClick={handleLatestContentImageSelection}
              type="nobutton"
            />
          )}

          <PreviewUploader
            allowableFiles={['image']}
            disabled={false}
            uploadInstructions={handleUploadImage}
            previewSrc={previewUploaderSrc()}
            selected={uploaderSelected}
            setSelectedAsset={handleUploaderSelection}
          />
        </div>

        <div className={kuiperClassNames('right-panel')}>
          <h3>Preview</h3>

          <div className={kuiperClassNames('preview-wrapper')}>
            <PreviewCard
              className={kuiperClassNames('preview-card')}
              moment={makePreviewMoment()}
            />
          </div>
          <Buttons
            className={kuiperClassNames('save-asset-buttons')}
            isLoading={loading}
            isNewAsset={saveable}
            onClose={() => {
              bbUnmountModal()
              onClose()
            }}
            onClickSave={onClickSave}
          />
        </div>
      </div>
    </AnalyticsLayer>
  )
}

MomentAssetPicker.propTypes = {
  bbUnmountModal: func,
  moment: object,
  onClose: func,
  onSaveAsset: func,
  uploadAsset: func,
}

export default MomentAssetPicker
